export const IMPACT_SUM_MAX_MESSAGE =
  "Impact of record level + impact of issue level must not exceed 1";

export const CATEGORY_WEIGHT_SUM_MAX_MESSAGE =
  "Missing + mismatch + validation category weights must not exceed 1";

/** Shared floating-point tolerance for category-weight totals. */
export const CATEGORY_WEIGHT_TOTAL_TOLERANCE = 0.001;

/** Keys that make up the comparison category-weight total. */
export const COMPARISON_CATEGORY_WEIGHT_KEYS = [
  "category_weight_missing",
  "category_weight_mismatch",
  "category_weight_validation",
] as const;

export interface NumericParam {
  key: string;
  label: string;
  min: number;
  max?: number;
}

export interface ScoringValueErrors {
  fieldErrors: Record<string, string>;
  impactSumError?: string;
  categoryWeightSumError?: string;
}

/** Canonical client-side validation for non-Formik scoring controls. */
export function validateScoringValues(
  params: NumericParam[],
  values: Record<string, unknown>,
): ScoringValueErrors {
  const fieldErrors: Record<string, string> = {};
  params.forEach((param) => {
    const value = Number(values[param.key]);
    if (values[param.key] === "" || !Number.isFinite(value)) {
      fieldErrors[param.key] = "Value is required";
    } else if (
      value < param.min ||
      (param.max !== undefined && value > param.max)
    ) {
      fieldErrors[param.key] =
        param.max === undefined
          ? `${param.label} must be at least ${param.min}`
          : `${param.label} must be between ${param.min} and ${param.max}`;
    }
  });

  const record = Number(values.impact_of_record_level);
  const issue = Number(values.impact_of_issue_level);
  const hasImpactPair = params.some(
    (param) => param.key === "impact_of_record_level",
  );
  // Comparison-only aggregate: the three category weights must not exceed 1.
  // Gated on the param set so Validation / Generic-RQ never inherit this rule.
  // Only reported once every weight is itself a finite number, so a cleared or
  // not-yet-seeded field surfaces its own field error instead of the aggregate.
  const hasCategoryWeights =
    COMPARISON_CATEGORY_WEIGHT_KEYS.every((key) =>
      params.some((param) => param.key === key),
    ) &&
    COMPARISON_CATEGORY_WEIGHT_KEYS.every(
      (key) => values[key] !== "" && Number.isFinite(Number(values[key])),
    );
  return {
    fieldErrors,
    impactSumError:
      hasImpactPair &&
      Number.isFinite(record) &&
      Number.isFinite(issue) &&
      record + issue > 1 + 1e-9
        ? IMPACT_SUM_MAX_MESSAGE
        : undefined,
    categoryWeightSumError:
      hasCategoryWeights && !isComparisonCategoryWeightTotalValid(values, "max")
        ? CATEGORY_WEIGHT_SUM_MAX_MESSAGE
        : undefined,
  };
}

/** Returns the cross-field impact-total error from Formik's error map. */
export function getImpactSumError(
  errors: Record<string, unknown>,
): string | undefined {
  return errors.impact_of_record_level === IMPACT_SUM_MAX_MESSAGE ||
    errors.impact_of_issue_level === IMPACT_SUM_MAX_MESSAGE
    ? IMPACT_SUM_MAX_MESSAGE
    : undefined;
}

/**
 * Total mode for the comparison category weights.
 * - `normalized` (default): the total must equal 1 — Add/Edit CRUD semantics.
 * - `max`: the total must not exceed 1 — recalculation override semantics.
 */
export type CategoryWeightTotalMode = "normalized" | "max";

/** Comparison category weights must form a valid total for the given mode. */
export function isComparisonCategoryWeightTotalValid(
  values: Record<string, unknown> | null | undefined,
  mode: CategoryWeightTotalMode = "normalized",
): boolean {
  if (!values) return false;
  const total =
    Number(values.category_weight_missing) +
    Number(values.category_weight_mismatch) +
    Number(values.category_weight_validation);
  if (!Number.isFinite(total)) return false;
  return mode === "max"
    ? total <= 1 + CATEGORY_WEIGHT_TOTAL_TOLERANCE
    : Math.abs(total - 1) < CATEGORY_WEIGHT_TOTAL_TOLERANCE;
}

/**
 * Resolves the values used by parent Save guards before a lazy scoring form
 * mounts and reports its current values.
 */
export function resolveScoringConfigValues<T>(
  values: T | null | undefined,
  defaults: T,
): T {
  return values ?? defaults;
}

/** Synchronous guard for parent Save/confirmation entry points. */
export function isSchemaValidSync(
  schema: { isValidSync: (values: unknown, options?: object) => boolean },
  values: unknown,
): boolean {
  try {
    return schema.isValidSync(values, { abortEarly: false });
  } catch {
    return false;
  }
}

interface SubmitState {
  isSubmitting: boolean;
  isLoading?: boolean;
  isValid: boolean;
  isEditMode: boolean;
  hasChanges: boolean;
  hasAdditionalInvalidState?: boolean;
}

/** Shared Add/Edit submit gate for Validation and Comparison scoring CRUD. */
export async function collectSchemaErrors(
  schema: { validate: (values: any, options: any) => Promise<any> },
  values: object,
): Promise<Record<string, string>> {
  try {
    await schema.validate(values, { abortEarly: false });
    return {};
  } catch (error: any) {
    const errors: Record<string, string> = {};
    (error.inner ?? []).forEach(
      ({ path, message }: { path?: string; message: string }) => {
        if (path && !errors[path]) errors[path] = message;
      },
    );
    return errors;
  }
}

export async function submitWhenValid(
  schema: { validate: (values: any, options: any) => Promise<any> },
  values: object,
  submit: () => void | Promise<void>,
): Promise<Record<string, string>> {
  const errors = await collectSchemaErrors(schema, values);
  if (Object.keys(errors).length === 0) await submit();
  return errors;
}

export function isScoringSubmitDisabled({
  isSubmitting,
  isLoading = false,
  isValid,
  isEditMode,
  hasChanges,
  hasAdditionalInvalidState = false,
}: SubmitState): boolean {
  return (
    isSubmitting ||
    isLoading ||
    !isValid ||
    hasAdditionalInvalidState ||
    (isEditMode && !hasChanges)
  );
}
